/**
 * P&L-only ("ghost") revenue adjustments.
 * Mirror of the sum used in functions/src/plReconciliation.ts — keep both in sync.
 */

export interface PlReconciliationLine {
  id: string;
  amount: number;
  title: string;
  reason: string;
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as Record<string, unknown>;
}

function isVoided(row: Record<string, unknown>): boolean {
  return Boolean(row.voidedAt);
}

/**
 * Ghost dollars that move Progress Billing contract value.
 * Every active row counts when the job has no proposal. A job with a proposal
 * counts only rows marked `appliesToProgressBilling`.
 */
export function sumContractPlReconciliations(list: unknown, hasProposal: boolean): number {
  if (!Array.isArray(list)) return 0;
  return list.reduce((sum, raw) => {
    const row = asRecord(raw);
    if (!row || isVoided(row)) return sum;
    if (hasProposal && row.appliesToProgressBilling !== true) return sum;
    const amount = Number(row.amount);
    return sum + (Number.isFinite(amount) ? Math.round(amount) : 0);
  }, 0);
}

/** Sum of non-voided adjustment amounts, rounded to whole dollars. */
export function sumActivePlReconciliations(list: unknown): number {
  if (!Array.isArray(list)) return 0;
  return list.reduce((sum, raw) => {
    const row = asRecord(raw);
    if (!row || isVoided(row)) return sum;
    const amount = Number(row.amount);
    return sum + (Number.isFinite(amount) ? Math.round(amount) : 0);
  }, 0);
}

/** Active lines for the Project P&L chip tooltip. */
export function activePlReconciliationLines(list: unknown): PlReconciliationLine[] {
  if (!Array.isArray(list)) return [];
  const lines: PlReconciliationLine[] = [];
  for (const raw of list) {
    const row = asRecord(raw);
    if (!row || isVoided(row)) continue;
    const amount = Number(row.amount);
    if (!Number.isFinite(amount) || Math.round(amount) === 0) continue;
    lines.push({
      id: typeof row.id === 'string' ? row.id : '',
      amount: Math.round(amount),
      title: typeof row.title === 'string' ? row.title : 'P&L adjustment',
      reason: typeof row.reason === 'string' ? row.reason : '',
    });
  }
  return lines;
}
